import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Briefcase, Building2, Activity as ActivityIcon,
  Bot, LogOut, RefreshCw, Loader2, Download, Search, Plus, X, Cpu, ScanSearch,
  FileText, Palette, Plug, Gavel, ShieldCheck, UserCog, KeyRound, Lock, Database, LayoutTemplate, Handshake,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { COMPANY, CANDIDATE_STAGES, JOB_STAGES } from "../../data/site";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { inputCls } from "../../components/forms/FormBits";
import { can, clearToken } from "../../lib/auth";
import AIAssistant from "./AIAssistant";
import Screening from "./Screening";
import ClientBriefs from "./ClientBriefs";
import BrandSettings from "./BrandSettings";
import Integrations from "./Integrations";
import MembershipQueue from "./MembershipQueue";
import MatchQueue from "./MatchQueue";
import JudgeKnowledgeBase from "./JudgeKnowledgeBase";
import WebsiteContent from "./WebsiteContent";
import UserManagement from "./UserManagement";
import RolesPermissions from "./RolesPermissions";
import SecuritySettings from "./SecuritySettings";
import ForcePasswordReset from "./ForcePasswordReset";
import CeoReauthModal from "../../components/admin/CeoReauthModal";
import { EngineActivity, NotificationBell } from "./EngineWidgets";

const TABS = [
  { key: "overview", label: "Executive Overview", icon: LayoutDashboard, module: "dashboard" },
  { key: "screening", label: "AI Screening", icon: ScanSearch, module: "screening" },
  { key: "matching", label: "AI Matching Judge", icon: Gavel, module: "matching" },
  { key: "judge_db", label: "Judge Knowledge Base", icon: Database, module: "judge_db" },
  { key: "candidates", label: "Candidate Intelligence", icon: Users, module: "candidates" },
  { key: "jobs", label: "Job Orders", icon: Briefcase, module: "jobs" },
  { key: "briefs", label: "Client Briefs", icon: FileText, module: "briefs" },
  { key: "employers", label: "Employers", icon: Building2, module: "employers" },
  { key: "activity", label: "System Activity", icon: ActivityIcon, module: "activity" },
  { key: "ai", label: "AI Assistant", icon: Bot, module: "ai_assistant" },
  { key: "integrations", label: "Integrations", icon: Plug, module: "integrations" },
  { key: "partnerships", label: "Partnership Applications", icon: Handshake, module: "partnerships" },
  { key: "brand", label: "Brand & Social", icon: Palette, module: "brand" },
  { key: "content", label: "Website Content", icon: LayoutTemplate, module: "content" },
  { key: "users", label: "User Management", icon: UserCog, module: "users" },
  { key: "roles", label: "Roles & Permissions", icon: ShieldCheck, module: "roles" },
  { key: "security", label: "Security", icon: KeyRound, module: "security" },
];

const CAND_FIELDS = [
  ["full_name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["location", "Location"],
  ["job_title", "Job title"], ["profession", "Profession"], ["medical_specialty", "Specialty"],
  ["years_experience", "Experience"], ["education", "Education"], ["nmbi_status", "NMBI"],
  ["gmc_status", "NMC/GMC"], ["hcpc_status", "HCPC/CORU"], ["visa_status", "Visa"],
  ["right_to_work", "Right to work"], ["document_expiry", "Doc expiry"], ["candidate_source", "Source"],
];
const JOB_FIELDS = [
  ["job_title", "Job title"], ["company_name", "Company"], ["contact_person", "Contact"],
  ["contact_email", "Email"], ["profession", "Profession"], ["medical_specialty", "Specialty"],
  ["number_of_vacancies", "Vacancies"], ["location", "Location"], ["salary", "Salary"],
  ["contract_type", "Contract"], ["working_hours", "Hours"], ["shift_pattern", "Shift"],
  ["start_date", "Start date"], ["required_registration", "Registration"], ["client_type", "Client type"],
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-border bg-white p-5">
    <p className={`font-serif text-3xl ${accent || "text-navy"}`}>{value ?? 0}</p>
    <p className="mt-1 text-xs font-medium text-muted-foreground">{label}</p>
  </div>
);

const PipelineBar = ({ title, data, stages, total }) => (
  <div className="rounded-2xl border border-border bg-white p-6">
    <h4 className="font-serif text-lg text-navy">{title}</h4>
    <div className="mt-4 space-y-3">
      {stages.map((s) => {
        const v = data?.[s] ?? 0;
        const pct = total ? Math.round((v / total) * 100) : 0;
        return (
          <div key={s}>
            <div className="flex justify-between text-xs text-charcoal/70"><span>{s}</span><span className="font-semibold">{v}</span></div>
            <div className="mt-1 h-2 rounded-full bg-secondary"><div className="h-2 rounded-full bg-gold" style={{ width: `${pct}%` }} /></div>
          </div>
        );
      })}
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [focusMatchId, setFocusMatchId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [catalog, setCatalog] = useState({ modules: [], actions: [] });

  const openMatch = useCallback((matchId) => {
    setTab("matching");
    setQ(""); setStatusFilter("");
    setFocusMatchId(matchId);
  }, []);

  const logout = useCallback(() => {
    api.post("/ceo/session/revoke").catch(() => {}).finally(() => { sessionStorage.removeItem("ceo_csrf"); clearToken(); navigate("/partner-dashboard"); });
  }, [navigate]);

  const loadMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setCurrentUser(data.user);
    setCatalog({ modules: data.modules, actions: data.actions });
    return data.user;
  }, []);

  const loadOverview = useCallback(async () => {
    try { const { data } = await api.get("/admin/overview"); setOverview(data); }
    catch (e) { if (e.response?.status === 401) logout(); else if (e.response?.status === 403) navigate("/admin/gate", { replace: true }); }
  }, [logout, navigate]);

  const loadTab = useCallback(async (key, query = "", status = "") => {
    setLoading(true);
    try {
      let url = null;
      if (key === "candidates") url = `/admin/intel/candidates?q=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}`;
      else if (key === "jobs") url = `/admin/intel/jobs?q=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}`;
      else if (key === "employers") url = `/admin/intel/employers`;
      else if (key === "activity") url = `/admin/activity`;
      if (url) { const { data } = await api.get(url); setList(data); }
    } catch (e) { if (e.response?.status === 401) logout(); else if (e.response?.status === 403) navigate("/admin/gate", { replace: true }); else toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [logout, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("azimi_admin_token")) { navigate("/partner-dashboard"); return; }
    loadMe().then((u) => {
      setReady(true);
      // land on first permitted tab
      const first = TABS.find((t) => u.is_owner || can(u, t.module, "view"));
      if (first && first.key === "overview") loadOverview();
      else if (first) setTab(first.key);
    }).catch(() => logout());
  }, [navigate, logout, loadMe, loadOverview]);

  const switchTab = (key) => {
    setTab(key); setQ(""); setStatusFilter("");
    if (key === "overview") loadOverview();
    else if (!["ai", "screening", "briefs", "brand", "integrations", "matching", "users", "roles", "security", "partnerships"].includes(key)) loadTab(key);
  };

  const visibleTabs = TABS.filter((t) => currentUser && (currentUser.is_owner || can(currentUser, t.module, "view")));

  const openRecord = async (rec) => {
    setSelected(rec);
    setEditing({ ...rec });
  };

  const saveRecord = async () => {
    setSaving(true);
    try {
      const isCand = tab === "candidates";
      const base = isCand ? "/admin/intel/candidates" : "/admin/intel/jobs";
      const isNew = !editing.id;
      if (isNew) await api.post(base, editing);
      else await api.patch(`${base}/${editing.id}`, editing);
      toast.success(isNew ? "Record created" : "Record updated");
      setSelected(null); setEditing(null);
      loadTab(tab, q, statusFilter); loadOverview();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const quickStatus = async (rec, value) => {
    try {
      const isCand = tab === "candidates";
      const base = isCand ? "/admin/intel/candidates" : "/admin/intel/jobs";
      await api.patch(`${base}/${rec.id}`, isCand ? { pipeline_status: value } : { status: value });
      toast.success("Stage updated");
      loadTab(tab, q, statusFilter); loadOverview();
    } catch { toast.error("Update failed"); }
  };

  const runScreening = async (id) => {
    try {
      await api.post(`/admin/screening/analyze/${id}`, {});
      toast.success("Screening started — see AI Screening → Review Queue");
    } catch { toast.error("Could not start screening"); }
  };

  const downloadDoc = async (fileId, filename) => {
    try {
      const res = await api.get(`/admin/document/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "document";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!ready) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;

  if (currentUser?.must_reset_password) {
    return <ForcePasswordReset onLogout={logout} onDone={() => loadMe().then((u) => { if (u.is_owner || can(u, "dashboard", "view")) loadOverview(); })} />;
  }

  const stages = tab === "candidates" ? CANDIDATE_STAGES : JOB_STAGES;
  const statusKey = tab === "candidates" ? "pipeline_status" : "status";
  const fields = tab === "candidates" ? CAND_FIELDS : JOB_FIELDS;

  return (
    <div className="min-h-screen bg-background">
      <CeoReauthModal />
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-gold/30 bg-navy shadow-[0_1px_0_0_rgba(194,117,26,0.25)]">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logo} alt={COMPANY.name} className="h-10 w-10 rounded-full object-cover ring-2 ring-gold/40" />
            <div>
              <p className="font-serif text-lg leading-tight text-white">{COMPANY.name}</p>
              <p className="text-xs font-medium tracking-wide text-gold-light">CEO Control Room · Recruitment Intelligence</p>
              <p className="text-[11px] text-white/60" data-testid="ceo-office-locations">Dublin, Ireland Office · London, UK Office</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell onOpenMatch={openMatch} />
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10" data-testid="admin-logout">
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
        <div className="h-0.5 w-full bg-gradient-to-r from-gold/0 via-gold to-gold/0" />
      </header>

      <div className="mx-auto flex max-w-[1500px] gap-6 px-6 py-6">
        {/* Sidebar */}
        <aside className="hidden w-60 shrink-0 lg:block">
          <nav className="sticky top-20 space-y-1">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => switchTab(t.key)} className={`flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.key ? "bg-navy text-white" : "text-charcoal/70 hover:bg-white"}`} data-testid={`tab-${t.key}`}>
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Mobile tabs */}
        <div className="fixed inset-x-0 bottom-0 z-30 flex justify-around overflow-x-auto border-t border-border bg-white py-1 lg:hidden">
          {visibleTabs.map((t) => { const Icon = t.icon; return (
            <button key={t.key} onClick={() => switchTab(t.key)} className={`flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${tab === t.key ? "text-navy" : "text-charcoal/50"}`}>
              <Icon className="h-4 w-4" /> {t.label.split(" ")[0]}
            </button>
          ); })}
        </div>

        <main className="min-w-0 flex-1 pb-20 lg:pb-0">
          {/* OVERVIEW */}
          {tab === "overview" && overview && (
            <div className="space-y-6" data-testid="overview-panel">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-navy">Executive Overview</h2>
                <button onClick={loadOverview} className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm text-charcoal hover:border-navy"><RefreshCw className="h-4 w-4" /> Refresh</button>
              </div>
              <EngineActivity />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Total Candidates" value={overview.overview.total_candidates} />
                <StatCard label="New Candidates" value={overview.overview.new_candidates} accent="text-gold-dark" />
                <StatCard label="Screened Candidates" value={overview.overview.screened_candidates} accent="text-success" />
                <StatCard label="Compliance Captured" value={overview.overview.compliance_captured} />
                <StatCard label="Total Job Orders" value={overview.overview.total_jobs} />
                <StatCard label="Open Jobs" value={overview.overview.open_jobs} accent="text-gold-dark" />
                <StatCard label="Ready for Review" value={overview.overview.ready_for_review_matches} accent="text-success" />
                <StatCard label="Database Events" value={overview.system_monitoring.database_activity} />
              </div>
              <div className="grid gap-6 lg:grid-cols-2">
                <PipelineBar title="Candidate Pipeline" data={overview.candidate_pipeline} stages={CANDIDATE_STAGES} total={overview.overview.total_candidates} />
                <PipelineBar title="Job Order Pipeline" data={overview.job_pipeline} stages={JOB_STAGES} total={overview.overview.total_jobs} />
              </div>
              <div className="rounded-2xl border border-border bg-white p-6">
                <div className="flex items-center gap-2"><Cpu className="h-5 w-5 text-gold" /><h4 className="font-serif text-lg text-navy">Recruitment Intelligence Framework</h4></div>
                <p className="mt-1 text-xs text-muted-foreground">A 16-agent framework, healthcare-first and multi-sector ready. Every autonomous action routes through the Human Approval Layer before anything is saved.</p>
                <div className="mt-4 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
                  {overview.agents.map((a) => (
                    <div key={a.id} className="rounded-xl border border-border bg-background px-3 py-2.5" data-testid={`agent-${a.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-charcoal">{a.id}. {a.name}</span>
                      </div>
                      <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.status === "Active" ? "bg-success/15 text-success" : a.status === "Beta" ? "bg-gold/15 text-gold-dark" : "bg-secondary text-charcoal/50"}`}>{a.status} · {a.layer}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* LIST TABS: candidates / jobs */}
          {(tab === "candidates" || tab === "jobs") && (
            <div data-testid={`${tab}-panel`}>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-serif text-2xl text-navy">{tab === "candidates" ? "Candidate Intelligence Database" : "Job Order Intelligence Database"}</h2>
                <button onClick={() => { setEditing(tab === "candidates" ? { pipeline_status: CANDIDATE_STAGES[0] } : { status: JOB_STAGES[0] }); setSelected({}); }} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="record-create">
                  <Plus className="h-4 w-4" /> New Record
                </button>
              </div>
              <div className="mb-4 flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal/40" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadTab(tab, q, statusFilter)} placeholder="Search name, email, specialty, location…" className="w-full rounded-lg border border-border bg-white py-2.5 pl-10 pr-4 text-sm focus:border-navy focus:outline-none" data-testid="search-input" />
                </div>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); loadTab(tab, q, e.target.value); }} className="rounded-lg border border-border bg-white px-4 py-2.5 text-sm" data-testid="status-filter">
                  <option value="">All stages</option>
                  {stages.map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => loadTab(tab, q, statusFilter)} className="rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-semibold text-charcoal hover:border-navy">Search</button>
              </div>

              {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
                : list.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-sm text-muted-foreground">No records yet.</div>
                : (
                <div className="space-y-3" data-testid="record-list">
                  {list.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-border bg-white p-5 transition-shadow hover:shadow-sm">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <button onClick={() => openRecord(r)} className="text-left" data-testid={`record-${r.id}`}>
                          <p className="font-serif text-lg text-navy">{tab === "candidates" ? r.full_name : r.job_title}</p>
                          <p className="text-sm text-muted-foreground">
                            {tab === "candidates" ? `${r.profession || ""} · ${r.medical_specialty || ""} · ${r.location || ""}` : `${r.company_name || ""} · ${r.profession || ""} · ${r.location || ""}`}
                          </p>
                          <p className="mt-1 text-xs text-charcoal/50">Added {fmt(r.created_at)}</p>
                        </button>
                        <div className="flex items-center gap-2">
                          {tab === "candidates" && (
                            <button onClick={() => runScreening(r.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-gold/40 px-3 py-1.5 text-xs font-semibold text-gold-dark hover:bg-gold/5" data-testid={`screen-${r.id}`}>Screen</button>
                          )}
                          {tab === "candidates" && r.cv?.file_id && (
                            <button onClick={() => downloadDoc(r.cv.file_id, r.cv.original_filename)} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-navy hover:border-navy" data-testid={`cv-${r.id}`}><Download className="h-3.5 w-3.5" /> CV</button>
                          )}
                          <select value={r[statusKey]} onChange={(e) => quickStatus(r, e.target.value)} className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-charcoal" data-testid={`stage-${r.id}`}>
                            {stages.map((s) => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-medium text-charcoal/60">Recruitment Intelligence · human-approved</span>
                        {(r.skills || r.required_skills || []).slice(0, 4).map((s) => <span key={s} className="rounded-full bg-navy/5 px-2.5 py-0.5 text-[11px] text-navy">{s}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* EMPLOYERS */}
          {tab === "employers" && (
            <div data-testid="employers-panel">
              <h2 className="mb-5 font-serif text-2xl text-navy">Employers</h2>
              {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
                : list.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-sm text-muted-foreground">No employers yet.</div>
                : <div className="grid gap-3 sm:grid-cols-2">{list.map((e) => (
                    <div key={e.id} className="rounded-2xl border border-border bg-white p-5">
                      <p className="font-serif text-lg text-navy">{e.company_name}</p>
                      <p className="text-sm text-muted-foreground">{e.client_type} · {e.location}</p>
                      <p className="mt-2 text-sm text-charcoal">{e.contact_person} · {e.contact_email}</p>
                    </div>
                  ))}</div>}
            </div>
          )}

          {/* ACTIVITY */}
          {tab === "activity" && (
            <div data-testid="activity-panel">
              <h2 className="mb-5 font-serif text-2xl text-navy">System Activity Log</h2>
              {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
                : list.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-sm text-muted-foreground">No activity yet.</div>
                : <div className="overflow-hidden rounded-2xl border border-border bg-white">
                    {list.map((a) => (
                      <div key={a.id} className="flex items-center justify-between border-b border-border px-5 py-3 text-sm last:border-0">
                        <span><span className="rounded bg-navy/5 px-2 py-0.5 text-xs font-semibold text-navy">{a.entity_type}</span> <span className="ml-2 text-charcoal">{a.action}</span> {a.details && <span className="text-muted-foreground">— {a.details}</span>}</span>
                        <span className="text-xs text-charcoal/50">{a.actor} · {fmt(a.created_at)}</span>
                      </div>
                    ))}
                  </div>}
            </div>
          )}

          {/* AI */}
          {tab === "ai" && <AIAssistant />}

          {/* AI SCREENING */}
          {tab === "screening" && <Screening />}

          {/* CLIENT BRIEFS */}
          {tab === "briefs" && <ClientBriefs />}

          {/* BRAND IDENTITY */}
          {tab === "brand" && <BrandSettings />}
          {tab === "content" && <WebsiteContent />}

          {/* INTEGRATIONS */}
          {tab === "integrations" && <Integrations />}
          {tab === "partnerships" && <MembershipQueue currentUser={currentUser} />}

          {/* CEO APPROVAL QUEUE — Deep Matching Engine */}
          {tab === "matching" && <MatchQueue focusMatchId={focusMatchId} onFocusHandled={() => setFocusMatchId(null)} />}
          {tab === "judge_db" && <JudgeKnowledgeBase currentUser={currentUser} onOpenMatch={openMatch} />}

          {/* USER MANAGEMENT / ROLES / SECURITY */}
          {tab === "users" && <UserManagement catalog={catalog} />}
          {tab === "roles" && <RolesPermissions catalog={catalog} />}
          {tab === "security" && <SecuritySettings currentUser={currentUser} refreshUser={loadMe} />}
        </main>
      </div>

      {/* Record view/edit modal */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEditing(null); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-navy">{editing?.id ? "Edit Record" : "New Record"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {fields.map(([key, label]) => (
                  <div key={key}>
                    <label className="mb-1 block text-xs font-semibold text-charcoal/70">{label}</label>
                    <input className={inputCls} value={editing[key] || ""} onChange={(e) => setEditing((p) => ({ ...p, [key]: e.target.value }))} data-testid={`edit-${key}`} />
                  </div>
                ))}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-charcoal/70">Pipeline stage</label>
                  <select className={inputCls} value={editing[statusKey] || stages[0]} onChange={(e) => setEditing((p) => ({ ...p, [statusKey]: e.target.value }))} data-testid="edit-stage">
                    {stages.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs text-muted-foreground">
                AI intelligence fields (summary, quality score, readiness, strengths, recommendations) are generated by our Recruitment Intelligence agents and always require human approval before they are saved.
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setSelected(null); setEditing(null); }} className="rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal">Cancel</button>
                <button onClick={saveRecord} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid="record-save">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
